import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { importsApi, type ExportRun, type ExportRunStatus } from "../api/imports";
import ManualLogin from "../components/ManualLogin";

const STATUS_COLORS: Record<ExportRunStatus, string> = {
  processing: "bg-amber-100 text-amber-800",
  ok: "bg-emerald-100 text-emerald-800",
  failed: "bg-rose-100 text-rose-800",
};

export default function AdminImports() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const runs = useQuery({
    queryKey: ["export-runs"],
    queryFn: importsApi.listExportRuns,
    refetchInterval: (q) =>
      q.state.data?.some((r) => r.status === "processing") ? 3000 : false,
  });

  const upload = useMutation({
    mutationFn: (file: File) => importsApi.uploadExport(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["export-runs"] }),
  });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      alert("Please select a .zip file from claude.ai → Data & Privacy → Export.");
      return;
    }
    upload.mutate(file);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Admin · Web Chat Imports</h2>
        <p className="mt-1 text-sm text-slate-500">
          Upload a ZIP from claude.ai → Settings → Data &amp; Privacy → Export data.
          Conversations, projects, and users are merged into the monitor.
        </p>
      </div>

      <ManualLogin />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          onFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => fileRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 text-sm transition ${
          dragging
            ? "border-brand-500 bg-brand-50"
            : "border-slate-300 bg-white hover:border-slate-400"
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <div className="text-slate-700">
          {upload.isPending ? "Uploading…" : "Drop a .zip here, or click to choose"}
        </div>
        {upload.isError && (
          <div className="mt-2 text-xs text-rose-600">
            Upload failed: {(upload.error as { message?: string })?.message ?? "unknown"}
          </div>
        )}
        {upload.isSuccess && (
          <div className="mt-2 text-xs text-emerald-700">
            Started export run #{upload.data.export_run_id} — processing in the background.
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Recent runs</h3>
        {runs.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
        {runs.data && (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Source</th>
                  <th className="px-4 py-2">By</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Users</th>
                  <th className="px-4 py-2 text-right">Convs</th>
                  <th className="px-4 py-2 text-right">Turns</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {runs.data.map((r) => (
                  <RunRow key={r.id} r={r} />
                ))}
                {runs.data.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-400">
                      No imports yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RunRow({ r }: { r: ExportRun }) {
  return (
    <tr>
      <td className="px-4 py-2 font-mono text-xs text-slate-500">{r.id}</td>
      <td className="px-4 py-2 text-xs text-slate-700">
        {new Date(r.uploaded_at).toLocaleString()}
      </td>
      <td className="px-4 py-2 text-xs text-slate-600">{r.source}</td>
      <td className="px-4 py-2 text-xs text-slate-700">{r.uploaded_by_email ?? "—"}</td>
      <td className="px-4 py-2">
        <span className={`rounded px-2 py-0.5 text-xs ${STATUS_COLORS[r.status]}`}>
          {r.status}
        </span>
        {r.status === "failed" && r.error_message && (
          <div className="mt-1 max-w-xs truncate font-mono text-xs text-rose-600" title={r.error_message}>
            {r.error_message}
          </div>
        )}
      </td>
      <td className="px-4 py-2 text-right text-xs">{r.users_created}</td>
      <td className="px-4 py-2 text-right text-xs">{r.conversations_imported}</td>
      <td className="px-4 py-2 text-right text-xs">{r.turns_imported}</td>
    </tr>
  );
}
